/**
 * Filter fields for the /reports list (#239) — the same chip-and-query
 * surface the feedback queue uses, pointed at report summaries.
 *
 * WHY HAND-WRITTEN RATHER THAN METADATA-DRIVEN
 * Which fields can be filtered, and with which vocabulary, is behaviour: the
 * status and audience vocabularies are closed sets the library already owns
 * (AUDIENCES / AUDIENCE_LABELS), and categories are whatever the loaded
 * reports use. The LABELS are data, though: where the list's column metadata
 * (`table_name=broker_deal_reports`) names a field, the chip uses that name,
 * so renaming a column on Admin → Field Settings renames its filter too.
 *
 * AUDIENCE IS OFFERED ONLY WHEN THE ROWS CARRY IT. The server leaves
 * `audience` off external users' summaries; a field (or an "is any of" list)
 * for a value they never receive would hint at a split they are not told
 * about, and could only ever match nothing.
 *
 * The output is the shape `WigFilterPanel` wants, and the queries it emits go
 * through `filterRows` (client-side): the report list arrives whole and small.
 */
import {
  AUDIENCES,
  AUDIENCE_LABELS,
  usedCategories,
} from "@warwick-investment-group/wig-react-library/reports";

/** The report's `status` values, in the order the picker offers them. */
export const REPORT_STATUS_OPTIONS = [
  { label: "Draft", value: "draft" },
  { label: "Published", value: "published" },
];

/** Does any row carry an audience? (External users' rows never do.) */
export const rowsCarryAudience = (rows) =>
  Array.isArray(rows) &&
  rows.some((r) => r && Object.prototype.hasOwnProperty.call(r, "audience"));

/**
 * @param {object} opts
 * @param {Array}  opts.rows        the loaded report summaries
 * @param {Array}  opts.columnMeta  the list's UI metadata rows ({field, label})
 * @returns {Array} WigFilterField[]
 */
export const buildReportsFilterFields = ({ rows = [], columnMeta = [] } = {}) => {
  const labels = new Map(
    (Array.isArray(columnMeta) ? columnMeta : [])
      .filter((m) => m && m.field && m.label)
      .map((m) => [String(m.field), String(m.label)])
  );
  const labelOf = (field, fallback) => labels.get(field) ?? fallback;

  return [
    { name: "name", label: labelOf("name", "Name"), inputType: "text" },
    // "select" over "singleSelect" for the same reason as the feedback queue:
    // it offers is / is not / is any of / is none of.
    {
      name: "status",
      label: labelOf("status", "Status"),
      inputType: "select",
      values: REPORT_STATUS_OPTIONS,
    },
    {
      name: "category",
      label: labelOf("category", "Category"),
      inputType: "select",
      values: usedCategories(rows).map((c) => ({ label: c, value: c })),
    },
    ...(rowsCarryAudience(rows)
      ? [
          {
            name: "audience",
            label: labelOf("audience", "Audience"),
            inputType: "select",
            values: AUDIENCES.map((a) => ({ label: AUDIENCE_LABELS[a] ?? a, value: a })),
          },
        ]
      : []),
    { name: "description", label: labelOf("description", "Description"), inputType: "text" },
  ];
};

/**
 * The list's default order: by category (case-insensitive, no category
 * last — as the old category grouping put "Uncategorized" last), then name.
 * Returns a new array; the input is not touched.
 */
export const sortReportsDefault = (rows) => {
  const key = (v) => (v == null ? "" : String(v).trim());
  const cmp = (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" });
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const ca = key(a?.category);
    const cb = key(b?.category);
    if (!ca !== !cb) return ca ? -1 : 1;
    return cmp(ca, cb) || cmp(key(a?.name), key(b?.name));
  });
};

export default buildReportsFilterFields;
